/**
 * Source types and their field requirements for guided citation entry.
 */

export type FieldRequirement = "required" | "recommended" | "optional";

export interface FieldConfig {
  key: string;
  label: string;
  placeholder?: string;
  requirement: FieldRequirement;
  helpText?: string;
}

export interface SourceType {
  id: string;
  name: string;
  icon: string; // Lucide icon name
  description: string;
  fields: FieldConfig[];
  cslType: string; // Maps to CSL type
}

export const SOURCE_TYPES: SourceType[] = [
  {
    id: "website",
    name: "Website",
    icon: "Globe",
    description: "Web page, blog post, or online article",
    cslType: "webpage",
    fields: [
      {
        key: "title",
        label: "Page Title",
        requirement: "required",
        placeholder: "Enter the page title",
      },
      {
        key: "author",
        label: "Author",
        requirement: "recommended",
        placeholder: "e.g. John Smith or Smith, J.",
        helpText: "Leave blank if no author is listed",
      },
      {
        key: "containerTitle",
        label: "Website Name",
        requirement: "recommended",
        placeholder: "e.g. BBC News, Wikipedia",
      },
      {
        key: "issued",
        label: "Publication Date",
        requirement: "recommended",
        placeholder: "e.g. 2024-01-15 or 2024",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "required",
        placeholder: "https://...",
      },
      {
        key: "accessed",
        label: "Access Date",
        requirement: "recommended",
        placeholder: "e.g. 2024-01-20",
        helpText: "When you accessed the page",
      },
    ],
  },
  {
    id: "book",
    name: "Book",
    icon: "BookOpen",
    description: "Physical or digital book",
    cslType: "book",
    fields: [
      {
        key: "title",
        label: "Book Title",
        requirement: "required",
        placeholder: "Enter the book title",
      },
      {
        key: "author",
        label: "Author(s)",
        requirement: "required",
        placeholder: "e.g. John Smith or Smith, J.",
      },
      {
        key: "publisher",
        label: "Publisher",
        requirement: "required",
        placeholder: "e.g. Oxford University Press",
      },
      {
        key: "issued",
        label: "Publication Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "edition",
        label: "Edition",
        requirement: "optional",
        placeholder: "e.g. 2nd",
      },
      {
        key: "ISBN",
        label: "ISBN",
        requirement: "optional",
        placeholder: "e.g. 978-0-123456-78-9",
      },
      {
        key: "page",
        label: "Page(s)",
        requirement: "optional",
        placeholder: "e.g. 45-67",
      },
    ],
  },
  {
    id: "journal",
    name: "Journal Article",
    icon: "FileText",
    description: "Academic journal or peer-reviewed article",
    cslType: "article-journal",
    fields: [
      {
        key: "title",
        label: "Article Title",
        requirement: "required",
        placeholder: "Enter the article title",
      },
      {
        key: "author",
        label: "Author(s)",
        requirement: "required",
        placeholder: "e.g. Smith, J. & Jones, M.",
      },
      {
        key: "containerTitle",
        label: "Journal Name",
        requirement: "required",
        placeholder: "e.g. Nature, Science",
      },
      {
        key: "volume",
        label: "Volume",
        requirement: "required",
        placeholder: "e.g. 15",
      },
      {
        key: "issue",
        label: "Issue",
        requirement: "recommended",
        placeholder: "e.g. 3",
      },
      {
        key: "page",
        label: "Pages",
        requirement: "required",
        placeholder: "e.g. 123-145",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "DOI",
        label: "DOI",
        requirement: "recommended",
        placeholder: "e.g. 10.1234/example",
        helpText: "Digital Object Identifier",
      },
    ],
  },
  {
    id: "news",
    name: "News Article",
    icon: "Newspaper",
    description: "Newspaper or news website article",
    cslType: "article-newspaper",
    fields: [
      {
        key: "title",
        label: "Article Title",
        requirement: "required",
        placeholder: "Enter the headline",
      },
      {
        key: "author",
        label: "Author",
        requirement: "recommended",
        placeholder: "e.g. John Smith",
      },
      {
        key: "containerTitle",
        label: "Publication",
        requirement: "required",
        placeholder: "e.g. The New York Times",
      },
      {
        key: "issued",
        label: "Date",
        requirement: "required",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "recommended",
        placeholder: "https://...",
      },
      {
        key: "section",
        label: "Section",
        requirement: "optional",
        placeholder: "e.g. Business, Sports",
      },
    ],
  },
  {
    id: "video",
    name: "Video",
    icon: "Video",
    description: "YouTube, Vimeo, or other online video",
    cslType: "motion_picture",
    fields: [
      {
        key: "title",
        label: "Video Title",
        requirement: "required",
        placeholder: "Enter the video title",
      },
      {
        key: "author",
        label: "Creator/Channel",
        requirement: "required",
        placeholder: "e.g. TED, Kurzgesagt",
      },
      {
        key: "containerTitle",
        label: "Platform",
        requirement: "recommended",
        placeholder: "e.g. YouTube, Vimeo",
      },
      {
        key: "issued",
        label: "Upload Date",
        requirement: "required",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "required",
        placeholder: "https://...",
      },
      {
        key: "duration",
        label: "Duration",
        requirement: "optional",
        placeholder: "e.g. 12:34",
      },
    ],
  },
  {
    id: "podcast",
    name: "Podcast",
    icon: "Podcast",
    description: "Podcast episode or audio show",
    cslType: "broadcast",
    fields: [
      {
        key: "title",
        label: "Episode Title",
        requirement: "required",
        placeholder: "Enter the episode title",
      },
      {
        key: "author",
        label: "Host(s)",
        requirement: "required",
        placeholder: "e.g. Joe Rogan",
      },
      {
        key: "containerTitle",
        label: "Podcast Name",
        requirement: "required",
        placeholder: "e.g. The Joe Rogan Experience",
      },
      {
        key: "issued",
        label: "Air Date",
        requirement: "required",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "recommended",
        placeholder: "https://...",
      },
      {
        key: "number",
        label: "Episode Number",
        requirement: "optional",
        placeholder: "e.g. 123",
      },
    ],
  },
  {
    id: "report",
    name: "Report",
    icon: "ClipboardList",
    description: "Technical report, white paper, or working paper",
    cslType: "report",
    fields: [
      {
        key: "title",
        label: "Report Title",
        requirement: "required",
        placeholder: "Enter the report title",
      },
      {
        key: "author",
        label: "Author(s)",
        requirement: "required",
        placeholder: "e.g. World Health Organization",
      },
      {
        key: "publisher",
        label: "Institution/Publisher",
        requirement: "required",
        placeholder: "e.g. WHO, NASA",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "number",
        label: "Report Number",
        requirement: "optional",
        placeholder: "e.g. TR-2024-01",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "optional",
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "thesis",
    name: "Thesis/Dissertation",
    icon: "GraduationCap",
    description: "Master's thesis or doctoral dissertation",
    cslType: "thesis",
    fields: [
      {
        key: "title",
        label: "Thesis Title",
        requirement: "required",
        placeholder: "Enter the thesis title",
      },
      {
        key: "author",
        label: "Author",
        requirement: "required",
        placeholder: "e.g. John Smith",
      },
      {
        key: "publisher",
        label: "University",
        requirement: "required",
        placeholder: "e.g. Harvard University",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "genre",
        label: "Degree Type",
        requirement: "recommended",
        placeholder: "e.g. Ph.D., Master's",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "optional",
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "chapter",
    name: "Book Chapter",
    icon: "BookMarked",
    description: "Chapter in an edited book",
    cslType: "chapter",
    fields: [
      {
        key: "title",
        label: "Chapter Title",
        requirement: "required",
        placeholder: "Enter the chapter title",
      },
      {
        key: "author",
        label: "Chapter Author(s)",
        requirement: "required",
        placeholder: "e.g. John Smith",
      },
      {
        key: "containerTitle",
        label: "Book Title",
        requirement: "required",
        placeholder: "Enter the book title",
      },
      {
        key: "editor",
        label: "Editor(s)",
        requirement: "recommended",
        placeholder: "e.g. Jane Doe",
      },
      {
        key: "publisher",
        label: "Publisher",
        requirement: "required",
        placeholder: "e.g. Oxford University Press",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "page",
        label: "Pages",
        requirement: "required",
        placeholder: "e.g. 123-145",
      },
    ],
  },
  {
    id: "conference",
    name: "Conference Paper",
    icon: "Users",
    description: "Paper presented at a conference",
    cslType: "paper-conference",
    fields: [
      {
        key: "title",
        label: "Paper Title",
        requirement: "required",
        placeholder: "Enter the paper title",
      },
      {
        key: "author",
        label: "Author(s)",
        requirement: "required",
        placeholder: "e.g. Smith, J. & Jones, M.",
      },
      {
        key: "containerTitle",
        label: "Conference Name",
        requirement: "required",
        placeholder: "e.g. ACM CHI 2024",
      },
      {
        key: "eventPlace",
        label: "Location",
        requirement: "recommended",
        placeholder: "e.g. New York, NY",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "page",
        label: "Pages",
        requirement: "optional",
        placeholder: "e.g. 123-145",
      },
      {
        key: "DOI",
        label: "DOI",
        requirement: "optional",
        placeholder: "e.g. 10.1234/example",
      },
    ],
  },
  {
    id: "film",
    name: "Film / Movie",
    icon: "Film",
    description: "Movie, documentary, or streaming film",
    cslType: "motion_picture",
    fields: [
      {
        key: "title",
        label: "Film Title",
        requirement: "required",
        placeholder: "Enter the film title",
      },
      {
        key: "author",
        label: "Director",
        requirement: "required",
        placeholder: "e.g. Christopher Nolan",
      },
      {
        key: "publisher",
        label: "Studio / Distributor",
        requirement: "recommended",
        placeholder: "e.g. Warner Bros, Netflix",
      },
      {
        key: "issued",
        label: "Release Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "optional",
        placeholder: "https://... (if streaming)",
      },
    ],
  },
  {
    id: "image",
    name: "Image / Photograph",
    icon: "Image",
    description: "Photograph, figure, chart, infographic, or artwork",
    cslType: "graphic",
    fields: [
      {
        key: "title",
        label: "Image Title",
        requirement: "required",
        placeholder: "Enter the image or artwork title",
      },
      {
        key: "author",
        label: "Creator / Photographer",
        requirement: "recommended",
        placeholder: "e.g. Ansel Adams",
        helpText: "Leave blank if no creator is credited",
      },
      {
        key: "genre",
        label: "Format / Medium",
        requirement: "recommended",
        placeholder: "e.g. Photograph, Infographic, Artwork, Chart",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "recommended",
        placeholder: "e.g. 2024",
      },
      {
        key: "publisher",
        label: "Source / Site",
        requirement: "recommended",
        placeholder: "e.g. Flickr, The Met, Getty Images",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "recommended",
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "social",
    name: "Social Media",
    icon: "MessageCircle",
    description: "Tweet, Facebook post, Instagram, etc.",
    cslType: "post",
    fields: [
      {
        key: "title",
        label: "Post Content / Title",
        requirement: "required",
        placeholder: "First 20 words of post...",
      },
      {
        key: "author",
        label: "Author / Username",
        requirement: "required",
        placeholder: "e.g. @elonmusk",
      },
      {
        key: "containerTitle",
        label: "Platform",
        requirement: "required",
        placeholder: "e.g. X (Twitter), Instagram",
      },
      {
        key: "issued",
        label: "Date Posted",
        requirement: "required",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "required",
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "encyclopedia",
    name: "Encyclopedia Entry",
    icon: "BookOpenCheck",
    description: "Wikipedia, Britannica, or other reference",
    cslType: "entry-encyclopedia",
    fields: [
      {
        key: "title",
        label: "Entry Title",
        requirement: "required",
        placeholder: "e.g. Climate change",
      },
      {
        key: "containerTitle",
        label: "Encyclopedia Name",
        requirement: "required",
        placeholder: "e.g. Wikipedia, Britannica",
      },
      {
        key: "author",
        label: "Author",
        requirement: "optional",
        placeholder: "Leave blank if none",
      },
      {
        key: "issued",
        label: "Date",
        requirement: "recommended",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "required",
        placeholder: "https://...",
      },
      {
        key: "accessed",
        label: "Access Date",
        requirement: "recommended",
        placeholder: "e.g. 2024-01-20",
      },
    ],
  },
  {
    id: "dictionary",
    name: "Dictionary Entry",
    icon: "BookA",
    description: "Merriam-Webster, OED, or other dictionary",
    cslType: "entry-dictionary",
    fields: [
      {
        key: "title",
        label: "Word / Entry",
        requirement: "required",
        placeholder: "e.g. Culture",
      },
      {
        key: "containerTitle",
        label: "Dictionary Name",
        requirement: "required",
        placeholder: "e.g. Merriam-Webster.com Dictionary",
      },
      {
        key: "author",
        label: "Author / Publisher",
        requirement: "optional",
        placeholder: "e.g. Merriam-Webster",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "recommended",
        placeholder: "e.g. 2024 (or leave blank for n.d.)",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "required",
        placeholder: "https://...",
      },
      {
        key: "accessed",
        label: "Access / Retrieval Date",
        requirement: "recommended",
        placeholder: "e.g. 2024-01-20",
      },
    ],
  },
  {
    id: "dataset",
    name: "Dataset",
    icon: "Database",
    description: "Research data, statistics, or database",
    cslType: "dataset",
    fields: [
      {
        key: "title",
        label: "Dataset Title",
        requirement: "required",
        placeholder: "Enter the dataset name",
      },
      {
        key: "author",
        label: "Creator / Organization",
        requirement: "required",
        placeholder: "e.g. World Bank",
      },
      {
        key: "publisher",
        label: "Publisher / Repository",
        requirement: "recommended",
        placeholder: "e.g. Figshare, Zenodo",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "required",
        placeholder: "e.g. 2024",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "recommended",
        placeholder: "https://...",
      },
      {
        key: "DOI",
        label: "DOI",
        requirement: "optional",
        placeholder: "e.g. 10.1234/example",
      },
    ],
  },
  {
    id: "software",
    name: "Software / App",
    icon: "Code",
    description: "Computer program, app, or code repository",
    cslType: "software",
    fields: [
      {
        key: "title",
        label: "Software Name",
        requirement: "required",
        placeholder: "e.g. Microsoft Excel, Python",
      },
      {
        key: "author",
        label: "Developer / Company",
        requirement: "required",
        placeholder: "e.g. Microsoft, Python Software Foundation",
      },
      {
        key: "version",
        label: "Version",
        requirement: "recommended",
        placeholder: "e.g. 3.12, 2024",
      },
      {
        key: "issued",
        label: "Year",
        requirement: "recommended",
        placeholder: "e.g. 2024",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "optional",
        placeholder: "https://...",
      },
    ],
  },
  {
    id: "interview",
    name: "Interview",
    icon: "Mic",
    description: "Personal or published interview",
    cslType: "interview",
    fields: [
      {
        key: "title",
        label: "Interview Title",
        requirement: "recommended",
        placeholder: "e.g. Interview with...",
      },
      {
        key: "author",
        label: "Interviewee",
        requirement: "required",
        placeholder: "Person being interviewed",
      },
      {
        key: "interviewer",
        label: "Interviewer",
        requirement: "recommended",
        placeholder: "Person conducting interview",
      },
      {
        key: "issued",
        label: "Date",
        requirement: "required",
        placeholder: "e.g. 2024-01-15",
      },
      {
        key: "containerTitle",
        label: "Publication / Program",
        requirement: "optional",
        placeholder: "e.g. 60 Minutes, The Guardian",
      },
      {
        key: "URL",
        label: "URL",
        requirement: "optional",
        placeholder: "https://...",
      },
    ],
  },
];

/**
 * Curated label → CSL type options for the manual-entry and edit-dialog source-type
 * selector. The default (first) option is `webpage`, matching the formatter's fallback in
 * `editableEntryToCsl` so users who don't pick get unchanged output. A short, high-traffic
 * list — not the full SOURCE_TYPES taxonomy — keeps the hand-entry UI fast.
 */
export interface ManualSourceTypeOption {
  label: string;
  cslType: string;
}

export const MANUAL_SOURCE_TYPE_OPTIONS: ManualSourceTypeOption[] = [
  // High-traffic types first (original list — order is UI muscle memory).
  { label: "Website", cslType: "webpage" },
  { label: "Book", cslType: "book" },
  { label: "Book chapter", cslType: "chapter" },
  { label: "Journal article", cslType: "article-journal" },
  { label: "Newspaper article", cslType: "article-newspaper" },
  { label: "Magazine article", cslType: "article-magazine" },
  { label: "Podcast", cslType: "broadcast" },
  { label: "Report", cslType: "report" },
  { label: "Conference paper", cslType: "paper-conference" },
  { label: "Thesis / dissertation", cslType: "thesis" },
  { label: "Film / Movie", cslType: "motion_picture" },
  { label: "Image / Photograph", cslType: "graphic" },
  { label: "Encyclopedia entry", cslType: "entry-encyclopedia" },
  { label: "Dictionary entry", cslType: "entry-dictionary" },
  { label: "Interview", cslType: "interview" },
  { label: "Song / Music", cslType: "song" },
  { label: "Social media post", cslType: "post" },
  // Video & audio
  { label: "YouTube / online video", cslType: "motion_picture" },
  { label: "Documentary", cslType: "motion_picture" },
  { label: "TV show episode", cslType: "broadcast" },
  { label: "Radio broadcast", cslType: "broadcast" },
  // Digital & software
  { label: "AI tool output (ChatGPT, Claude)", cslType: "software" },
  { label: "Software / computer program", cslType: "software" },
  { label: "Mobile app", cslType: "software" },
  { label: "Video game", cslType: "software" },
  { label: "Dataset", cslType: "dataset" },
  { label: "Blog post", cslType: "post-weblog" },
  { label: "Forum post / online comment", cslType: "post" },
  { label: "Wikipedia / wiki article", cslType: "entry-encyclopedia" },
  // Academic & grey literature
  { label: "Preprint (arXiv, bioRxiv)", cslType: "article" },
  { label: "Working paper", cslType: "report" },
  { label: "White paper", cslType: "report" },
  { label: "Press release", cslType: "report" },
  { label: "Government report", cslType: "report" },
  { label: "Technical standard (ISO, IEEE)", cslType: "standard" },
  { label: "Lecture notes / course materials", cslType: "document" },
  { label: "Grey literature / other document", cslType: "document" },
  // Legal & government
  { label: "Court case", cslType: "legal_case" },
  { label: "Law / statute", cslType: "legislation" },
  { label: "Bill (proposed legislation)", cslType: "bill" },
  { label: "Regulation", cslType: "regulation" },
  { label: "Treaty", cslType: "treaty" },
  { label: "Government hearing / testimony", cslType: "hearing" },
  { label: "Patent", cslType: "patent" },
  // Visual, performance & music
  { label: "Artwork / painting", cslType: "graphic" },
  { label: "Poster / infographic", cslType: "graphic" },
  { label: "Figure / chart", cslType: "figure" },
  { label: "Map", cslType: "map" },
  { label: "Musical score / sheet music", cslType: "musical_score" },
  { label: "Live performance / staged play", cslType: "performance" },
  { label: "Speech / lecture", cslType: "speech" },
  { label: "Presentation / slide deck", cslType: "speech" },
  { label: "Sermon", cslType: "speech" },
  // Print & archival
  { label: "E-book", cslType: "book" },
  { label: "Religious text (Bible, Quran)", cslType: "book" },
  { label: "Play (published text)", cslType: "book" },
  { label: "Edited collection / anthology", cslType: "collection" },
  { label: "Poem or short story (in a collection)", cslType: "chapter" },
  { label: "Manuscript (unpublished)", cslType: "manuscript" },
  { label: "Archival material", cslType: "manuscript" },
  { label: "Pamphlet / brochure", cslType: "pamphlet" },
  {
    label: "Personal communication (email, letter)",
    cslType: "personal_communication",
  },
  { label: "Review", cslType: "review" },
  { label: "Book review", cslType: "review-book" },
];

export function getSourceTypeById(id: string): SourceType | undefined {
  return SOURCE_TYPES.find((type) => type.id === id);
}

export function getSourceTypeByCslType(
  cslType: string
): SourceType | undefined {
  return SOURCE_TYPES.find((type) => type.cslType === cslType);
}

/**
 * Detect source type from a CSL type string.
 * Falls back to website if unknown.
 */
export function detectSourceType(cslType?: string): SourceType {
  if (!cslType) {
    return SOURCE_TYPES[0]; // website
  }
  const found = getSourceTypeByCslType(cslType);
  return found ?? SOURCE_TYPES[0];
}

/**
 * Entry with all possible fields for the wizard.
 */
export interface WizardEntry {
  title?: string;
  author?: string;
  issued?: string;
  URL?: string;
  publisher?: string;
  containerTitle?: string;
  type?: string;
  volume?: string;
  issue?: string;
  page?: string;
  DOI?: string;
  ISBN?: string;
  edition?: string;
  accessed?: string;
  section?: string;
  duration?: string;
  number?: string;
  genre?: string;
  editor?: string;
  eventPlace?: string;
  version?: string;
  interviewer?: string;
}

/**
 * Analyze an entry and return which fields are missing or empty.
 */
export function analyzeMissingFields(
  entry: WizardEntry,
  sourceType: SourceType
): { field: FieldConfig; status: "missing" | "filled" }[] {
  return sourceType.fields.map((field) => {
    const value = entry[field.key as keyof WizardEntry];
    const isFilled = value != null && String(value).trim() !== "";
    return {
      field,
      status: isFilled ? "filled" : "missing",
    };
  });
}

/**
 * Check if an entry has critical missing fields.
 * Returns true if any required field is missing.
 */
export function hasMissingRequiredFields(
  entry: WizardEntry,
  sourceType: SourceType
): boolean {
  const analysis = analyzeMissingFields(entry, sourceType);
  return analysis.some(
    (a) => a.status === "missing" && a.field.requirement === "required"
  );
}

/**
 * Count missing fields by requirement level.
 */
export function countMissingFields(
  entry: WizardEntry,
  sourceType: SourceType
): { required: number; recommended: number; optional: number; total: number } {
  const analysis = analyzeMissingFields(entry, sourceType);
  const missing = analysis.filter((a) => a.status === "missing");
  return {
    required: missing.filter((a) => a.field.requirement === "required").length,
    recommended: missing.filter((a) => a.field.requirement === "recommended")
      .length,
    optional: missing.filter((a) => a.field.requirement === "optional").length,
    total: missing.length,
  };
}
